import { growthbook, gbReady } from '../lib/growthbookClient';
import {
  PAYWALL_NAV_CONFIG_FLAG,
  DEFAULT_NAV_CONFIG,
  resolveNavConfig,
  resolveAuthType,
} from './authType';

// Trans-LP analytics helpers (mirrors the swipeymatch / Match-LP pattern).
//
// All analytics flows through GTM (window.dataLayer) — this lander never talks
// to Mixpanel directly (no SDK / token). The GTM container GTM-N8LQSVFS is shared
// with the swipeymatch and Match-LP landers. `lp_source` lets the shared
// container tell this lander's traffic apart from the others.

const LP_SOURCE = 'createaitrans';

// `lander-content` — identifies which GrowthBook config variant is live, taken
// from the config's top-level `name` field (e.g. "default"). Sent on EVERY
// event alongside `lp_source`. Defaults to 'default' (the baked-in config)
// until GrowthBook resolves and the provider calls setLanderContent().
let landerContent = 'default';

// Called by GrowthBookProvider once the GB payload resolves, passing the
// config's `name`. No-ops on empty/missing names so events keep the default.
export function setLanderContent(name) {
  if (name) landerContent = name;
}

// `createaitrans-paywall-nav-config` — the resolved post-signup arm, sent on
// EVERY event under the GrowthBook flag's own key so the shared container reads the
// same key it already sees in `ab_testing`. Same value Everflow reports as sub7
// (utils/everflow) — one resolver, utils/authType, so GTM and Everflow can never
// disagree about which arm the user was in.
//
// Read live off the shared client instead of being pushed in from React: that keeps
// it correct for the sticky arms (resolveNavConfig) with no provider wiring. Stays
// empty until GB settles — an empty value means "not resolved yet", never a guess.
let gbSettled = false;
gbReady.then(() => { gbSettled = true }).catch(() => { gbSettled = true });

export const getPaywallNavArm = () => {
  if (!gbSettled) return '';
  try {
    return resolveAuthType(
      resolveNavConfig(
        growthbook.getFeatureValue(PAYWALL_NAV_CONFIG_FLAG, DEFAULT_NAV_CONFIG),
      ),
    );
  } catch (e) {
    return '';
  }
};

function push(event, props = {}) {
  if (typeof window === 'undefined') return;
  window.dataLayer = window.dataLayer || [];
  // Every event carries `lp_source` + `lander-content`. Spread caller props
  // last so an explicit override still wins.
  window.dataLayer.push({
    event,
    lp_source: LP_SOURCE,
    'lander-content': landerContent,
    [PAYWALL_NAV_CONFIG_FLAG]: getPaywallNavArm(),
    ...props,
  });
}

// First screen — the user picked their style and advanced. GTM-only.
export function trackStyleSelected(props = {}) {
  push('style_selected', props);
}

// Quiz steps 2–6. step_2..step_5 fire as each quiz step is completed; step_6
// fires when the Summary screen is shown. GTM-only.
export function trackStep(stepNumber, props = {}) {
  push(`step_${stepNumber}`, { step_number: stepNumber, ...props });
}

// User tapped the back arrow to return to the previous step. GTM-only.
export function trackClickBack(props = {}) {
  push('click_back', props);
}

// Registration form (AuthModal) opened — GTM/Ads event.
export function trackRegistrationModalOpened() {
  push('lander_registration_modal');
}

// Registration succeeded. Fires the GTM/Ads conversion event, including the
// user's email in the dataLayer when available.
export function trackRegistrationCompleted(email) {
  push('lander_registration_completed', email ? { email } : {});
}

// Login succeeded (password, magic-link, or OTP). GTM-only — NOT forwarded to
// Mixpanel.
export function trackLoginCompleted() {
  push('lander_login_completed');
}

// Forgot-password flow completed (new password set). GTM-only — NOT forwarded
// to Mixpanel.
export function trackForgotPassCompleted() {
  push('lander_forgot_pass_completed');
}
